import { createContext, useContext, useRef } from "react";
import { Auth } from "aws-amplify";
import { toast } from "react-toastify";

import { KLAVIYO_ENDPOINT } from "data/constants";
import { useAuth } from "./AuthHook";
import { translations } from "i18n";

const SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE =
  "SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE";
const SUBSCRIPTION_MODAL_WAS_DISPLAYED = "SUBSCRIPTION_MODAL_WAS_DISPLAYED";

const KlaviyoContext = createContext();

const useKlaviyo = () => {
  const context = useContext(KlaviyoContext);
  if (!context) {
    throw new Error(
      "`useKlaviyo` must be used within `KlaviyoContextProvider`"
    );
  }
  return context;
};

const KlaviyoContextProvider = ({ children }) => {
  const { authenticatedUser } = useAuth();

  const isFirstRun = useRef(true); // used to limit checkIsSubscribed call in MenuContainer

  // Simplify localStorage key management by directly using user email
  const getLocalStorageKey = (type, email) => `fp_klaviyo_${type}:${email}`;

  const checkIsSubscribed = async () => {
    if (!authenticatedUser) return false;
    const cognitoIdToken = (await Auth.currentSession())
      .getIdToken()
      .getJwtToken();
    try {
      const response = await fetch(KLAVIYO_ENDPOINT, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Cognito-Id-Token": cognitoIdToken,
          "x-project-origin": "feelhubx",
        },
      });
      const json = await response.json();
      const result = json.message.includes("YES");
      setNewsletterAccepted(result);
      return result;
    } catch (error) {
      console.error("Error checking subscription status:", error);
      return false;
    }
  };

  const changeSubscriptionStatus = async (action) => {
    if (!authenticatedUser) return;
    const cognitoIdToken = (await Auth.currentSession())
      .getIdToken()
      .getJwtToken();
    try {
      await fetch(KLAVIYO_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Cognito-Id-Token": cognitoIdToken,
          "x-project-origin": "feelhubx",
        },
        body: JSON.stringify({
          action,
        }),
      });
      setNewsletterAccepted(action === "subscribe");
      toast.success(
        translations(
          action === "subscribe"
            ? "Newsletter.CheckboxSub"
            : "Newsletter.CheckboxUnsub"
        )
      );
    } catch (error) {
      console.error("Error subscribing user:", error);
    }
  };

  const hasAccepted = () => {
    return !!localStorage.getItem(
      SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE
    );
  };

  const setAccepted = () => {
    localStorage.setItem(SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE, "1");
  };

  const removeAcceptedKey = () => {
    localStorage.removeItem(SUBSCRIPTION_TO_KLAVIYO_NEWSLETTER_TO_BE_DONE);
  };

  const checkIfSubscriptionModalWasDisplayed = () => {
    return !!localStorage.getItem(SUBSCRIPTION_MODAL_WAS_DISPLAYED);
  };

  const setSubscriptionModalWasDisplayed = () => {
    return !!localStorage.setItem(SUBSCRIPTION_MODAL_WAS_DISPLAYED, "1");
  };

  const hasNewsletterAccepted = () => {
    if (!authenticatedUser) return false;
    const newsletterKey = getLocalStorageKey(
      "newsletter",
      authenticatedUser.attributes.email
    );
    return localStorage.getItem(newsletterKey) === "true";
  };

  const setNewsletterAccepted = (value) => {
    if (!authenticatedUser) return;
    const newsletterKey = getLocalStorageKey(
      "newsletter",
      authenticatedUser.attributes.email
    );
    localStorage.setItem(newsletterKey, value);
  };

  const removeNewsletterAcceptedKey = () => {
    if (!authenticatedUser) return;
    const newsletterKey = getLocalStorageKey(
      "newsletter",
      authenticatedUser.attributes.email
    );
    localStorage.removeItem(newsletterKey);
  };

  return (
    <KlaviyoContext.Provider
      value={{
        subscribe: async () => await changeSubscriptionStatus("subscribe"),
        unsubscribe: async () => await changeSubscriptionStatus("unsubscribe"),
        checkIsSubscribed,
        hasAccepted,
        setAccepted,
        removeAcceptedKey,
        hasNewsletterAccepted,
        setNewsletterAccepted,
        checkIfSubscriptionModalWasDisplayed,
        setSubscriptionModalWasDisplayed,
        removeNewsletterAcceptedKey,
        isFirstRun,
      }}
    >
      {children}
    </KlaviyoContext.Provider>
  );
};

export { KlaviyoContextProvider, useKlaviyo };
